import React, { useState, useEffect, useMemo } from "react" // 26 Sep 2025 - added useMemo
import useTitle from "@/hooks/useTitle"
import { useNavigate } from "react-router"
import {
  PlusIcon,
  MinusIcon,
  TrashIcon,
  PencilSquareIcon,
} from "@heroicons/react/24/outline"
import {
  PlusCircleIcon,
  CalendarDaysIcon,
  ArrowUpCircleIcon,
  ClockIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/solid"
import ExploreOutlinedIcon from "@mui/icons-material/ExploreOutlined"
import PulseLoader from "react-spinners/PulseLoader"
import { formatDate, formatDateTime } from "@/utils/formatDate"
import { getTimeSince } from "@/utils/EtGetTimeSince"
import { differenceInDays } from 'date-fns' // Added 29 Jun 2024
import {
  useGetUserActionsQuery,
  useUpdateUserActionMutation, // 09 Jun 2024 - update mutation
  useDeleteUserActionMutation, // 16 May 2025
} from "./userActionsApiSlice" 

// 16 May 2025
import EtModalUpdateUserAction from '@/components/common/modal/EtModalUpdateUserAction'
import Modal from "@/components/common/modal/Modal";

// 09 Sep 2025
import { groupActionsByHierarchy } from "@/utils/groupActionsByHierarchy";

// 10 Sep 2025
import EtUserSessionActionCard from "@/components/actions/EtUserSessionActionCard"

// 14 Sep 2025
import EtGroupedLevelBlock from "@/components/actions/EtGroupedLevelBlock"
import EtGroupedActionTypeBlock from "@/components/actions/EtGroupedActionTypeBlock"

// 13 Sep 2025
import EtImprintMissionsSelfIcon1SVGComp from '@/assets/img/icons/svgComp/EtImprintMissionsSelfIcon1SVGComp'
import EtImprintMissionsClientsIcon1SVGComp from '@/assets/img/icons/svgComp/EtImprintMissionsClientsIcon1SVGComp'
import EtImprintMissionOrgIcon1SVGComp from '@/assets/img/icons/svgComp/EtImprintMissionOrgIcon1SVGComp'
import EtImprintMissionPartnerIcon1SVGComp from '@/assets/img/icons/svgComp/EtImprintMissionPartnerIcon1SVGComp'
import EtImprintMissionTeamIcon1SVGComp from '@/assets/img/icons/svgComp/EtImprintMissionTeamIcon1SVGComp'

import EtImprintVisionIcon2SVGComp from '@/assets/img/icons/svgComp/EtImprintVisionIcon2SVGComp'
import EtImprintMissionsIcon1SVGComp from '@/assets/img/icons/svgComp/EtImprintMissionsIcon1SVGComp'
import EtUserAcionGoalIcon1SVGComp from '@/assets/img/icons/svgComp/EtUserAcionGoalIcon1SVGComp'

// 26 Sep 2025
import moment from 'moment-timezone';
import jstz from 'jstimezonedetect';

// 22 Dec 2025
import { buildPerformanceIndex } from "@/utils/performance/buildPerformanceIndex";

// 30 Dec 2025
import { getCurrentRecurrenceLogEntry, getNextRecurrenceLogDate } from "@/utils/logUtils";


const EtUserActionListByGroupWithTabsComp = ({ userId, trailId, sessionId }) => {
  // First Created - 11 Sep 2024
  // Author - Atul Pandey
  // Inherited from EtUserActionListByGroupComp 
  // Purpose - to separate actions into Tabs based on actionCreatedBy field
  // Updated - 11 Sep 2024 - Added state for managing active tab
  // Updated - 12 Sep 2024 - added triggeringUrl for navigating back to the originating url
  // Updated - 18 Sep 2024 - Set the actionProgress to the current userActionProgress if not already set when a new note is added without updating progress
  // Updated - 18 Feb 2025 - Refined the JSX for displaying actionDesc
  // Updated - 16 May 2025 - Refined for Updating an action using Update Action modal component
  // Updated - 16 May 2025 - Refined for Deleting an action
  // Updated - 09 Sep 2025 - Refined for Re tabbing Induction vs. MyMisions - Self, Client, Org, Partner, Team and then by Vision --> Mission --> Goal --> Goal Period --> ActionTypeName --> Action
  // Updated - 10 Sep 2025 - used groupedActionsByHierarchy function for new tab grouping
  // Updated - 10 Sep 2025 - used EtUserSessionActionCard for displaying user actions
  // Updated - 13 Sep 2025 - refinement of the JSX with the new grouping
  // Updated - 13 Sep 2025 - further refinement of the JSX with the new grouping - to separate actionType from next level object at each focus level for MyMissions - vision (actions under actionType and missions connected to mission)
  // Updated - 14 Sep 2025 - Further refined the display
  // Updated - 15 Sep 2025 - Further refined the display & added the tab for Induction Trails
  // Updated - 16 Sep 2025 - Action count logic
  // Updated - 26 Sep 2025 - Action Progress Update for recurring actions - handleSave refactor (passed as onSave to the EtUserSessionActionCard)
  // Updated - 27 Sep 2025 - Fixed the issue with recurring log scaled progress cumulation
  // Updated - 28 Sep 2025 - Refined the handleNote and handleProgressChange to accommodate recurring logs
  // Updated - 18 Dec 2025 - fix for including actionNoteActualValue: cumulativeActualValue in handleSave for isRecurring
  // Updated - 22 Dec 2025 - refinements for Performance data display - roll ups and collective - buildPerformanceIndex utility
  // Updated - 30 Dec 2025 - fix - recurringLog (non Daily - get currentLog using the logUtils - getCurrentRecurrenceLogEntry )

  // 0 - Set Page Title
  useTitle("User Actions List By Group")

  // 1 - set page navigations
  const navigate = useNavigate()

  // 2a - Initialize showDetails state as an object where key is the session index
  const [showDetails, setShowDetails] = useState({})
  const [actionNotes, setActionNotes] = useState({}) // 09 Jun 2024
  const [actionProgress, setActionProgress] = useState({}) // 09 Jun 2024
  const [showGroup, setShowGroup] = useState({}); // 02 Sep 2024 To manage group toggling
  const [activeTab, setActiveTab] = useState('All'); // 11 Sep 2024 - Options: 'All', 'Self', 'Coach', 'Sponsor'

  // 16 May 2025 - State for Edit Modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);

  // 16 May 2025 - State for Delete Modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedActionToDelete, setSelectedActionToDelete] = useState(null);

  // 13 Sep 2025 - Tabs for new Grouping
  const [topLevelTab, setTopLevelTab] = useState('MyMissions'); // default to MyMissions
  const [selectedMissionFocusTab, setSelectedMissionFocusTab] = useState('org');
  const [showVision, setShowVision] = useState({});
  const [showMission, setShowMission] = useState({});
  const [showGoal, setShowGoal] = useState({});
  const [showGoalPeriod, setShowGoalPeriod] = useState({});

  const [visionTab, setVisionTab] = useState('visionActions'); // 14 Sep 2025
  const [missionTab, setMissionTab] = useState('missionActions'); // 14 Sep 2025
  const [goalTab, setGoalTab] = useState('goalActions'); // 14 Sep 2025
  const [goalPeriodTab, setGoalPeriodTab] = useState('goalPeriodActions'); // 14 Sep 2025

  // 26 Sep 2025 - actual Action Value update
  const [actionActualValue, setActionActualValue] = useState({});
  
  
  // 2b - Detect timezone
  const detectTimezone = () => {
    const timezone = jstz.determine();
    return timezone ? timezone.name() : 'UTC';
  }

  const [timezone, setTimezone] = useState(detectTimezone());
  
  useEffect(() => {
    const newTimezone = detectTimezone();
    if (newTimezone !== timezone) {
      setTimezone(newTimezone);
    }
  }, []);
  

  // 3 - Extract the user actions data
  const {
    data: userActionsData,
    isError,
    error,
    isLoading,
    refetch: refetchUserActions, // 16 May 2025
  } = useGetUserActionsQuery()
  // console.log("userActionsData", userActionsData)

  // 3a - Mutation for updating user action -- 09 Jun 2024
  const [updateUserAction] = useUpdateUserActionMutation()
  const [deleteUserAction] = useDeleteUserActionMutation(); // 16 May 2025

  // 3b - Load state from localStorage -- 11 Jun 2024
  useEffect(() => {
    const storedShowDetails = localStorage.getItem("showDetails")
    if (storedShowDetails) {
      setShowDetails(JSON.parse(storedShowDetails))
    }
  }, [])

  // 3c Save state to localStorage before page reload -- 11 Jun 2024
  useEffect(() => {
    const handleBeforeUnload = () => {
      localStorage.setItem("showDetails", JSON.stringify(showDetails))
    }

    window.addEventListener("beforeunload", handleBeforeUnload)
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload)
    }
  }, [showDetails])

  // 4 - Toggle Show function for userAction details -- updated -- 12 Jun 2024 --
  const handleToggleDetails = (actionId) => {
    setShowDetails((prev) => {
      const newShowDetails = { ...prev, [actionId]: !prev[actionId] }

      // Update the value attribute when details are shown
      if (!prev[actionId]) {
        const action = filteredUserActionsData?.find((a) => a.id === actionId)

        const slider = document.getElementById(`slider-${actionId}`)
        if (slider) {
          const initialValue =
            actionProgress[actionId] || action?.userActionProgress || 0
          slider.value = initialValue // Set the value attribute
        }
      }
      return newShowDetails
    })
  }

  // 5 - Create an object for setting inner HTML -- 18 Feb 2025
  const createMarkup = (htmlContent) => {
    const formattedContent = htmlContent.replace(/\n/g, '<br />'); // Convert \n to <br />
    // return { __html: htmlContent }  // 18 Feb 2025 - commented
    return { __html: formattedContent };
  }

  // 6a - Group user actions by actionType and activeTab
  const groupedUserActionsOld = userActionsData?.ids
  .map((id) => userActionsData.entities[id])
  .filter((action) => {
    // Common conditions for all tabs
    const commonConditions =
      action.userId._id === userId &&
      action.trailId._id === trailId &&
      action.sessionId._id === sessionId;

    // Filter based on activeTab value
    if (activeTab === 'Self') {
      return commonConditions && action.actionCreatedBy === 'Self';
    } else if (activeTab === 'Coach') {
      return commonConditions && action.actionCreatedBy === 'Coach';
    } else if (activeTab === 'Sponsor') {
      return commonConditions && action.actionCreatedBy === 'Sponsor';
    } else if (activeTab === 'Session') {
      return commonConditions && action.actionCreatedBy === 'Session';
    }else {
      // Show all actions when the 'All' tab is active
      return commonConditions;
    }
  })
  .reduce((acc, action) => {
    const actionType = action.actionTypeId?.actionTypeName || 'Unknown';
    if (!acc[actionType]) acc[actionType] = [];
    acc[actionType].push(action);
    return acc;
  }, {});

  // 6a - Group user actions by hierarchy using helper
  const allFilteredActions = userActionsData?.ids
    .map((id) => {
      const action = userActionsData.entities[id];

      // 🟢 Inject selectedFocus based on focus-linked Vision fields
      let selectedFocus = null;
      if (action.actionVisionClientIds?.length > 0) selectedFocus = "Client";
      else if (action.actionVisionOrgIds?.length > 0) selectedFocus = "Org";
      else if (action.actionVisionPartnerIds?.length > 0) selectedFocus = "Partner";
      else if (action.actionVisionTeamIds?.length > 0) selectedFocus = "Team";
      else if (action.actionVisionSelfIds?.length > 0) selectedFocus = "Self";

      return { ...action, selectedFocus };  // ✅ enrich action
    })
    .filter((action) => {
      const commonConditions =
        action.userId._id === userId &&
        action.trailId._id === trailId &&
        action.sessionId._id === sessionId;

      if (activeTab === 'Self') {
        return commonConditions && action.actionCreatedBy === 'Self';
      } else if (activeTab === 'Coach') {
        return commonConditions && action.actionCreatedBy === 'Coach';
      } else if (activeTab === 'Sponsor') {
        return commonConditions && action.actionCreatedBy === 'Sponsor';
      } else if (activeTab === 'Session') {
        return commonConditions && action.actionCreatedBy === 'Session';
      } else {
        return commonConditions;
      }
  });

  
// console.log("allFilteredActions", allFilteredActions)

  // 6b - Generate grouped structure
  // const groupedUserActions = groupActionsByHierarchy(allFilteredActions);

  // 26 Sep 2025
  const groupedUserActions = useMemo(() => {
  return groupActionsByHierarchy(allFilteredActions);
}, [allFilteredActions]);

  // console.log('groupedUserActions:', groupedUserActions)

  const topLevelGroups = Object.keys(groupedUserActions || {});

  // 16 Sep 2025 - Check if top-level "MyMissions" has direct actions (arrays directly under it)
  const myMissionsRootActions = Object.entries(groupedUserActions["MyMissions"] || {})
    .filter(([_, val]) => Array.isArray(val));

  // console.log('myMissionsRootActions', myMissionsRootActions?.length)

  const hasMyMissionsRootActions = myMissionsRootActions?.length > 0;

  const focusTabs = [
  hasMyMissionsRootActions && { key: 'all', label: 'All', Icon: EtImprintMissionsIcon1SVGComp },
  { key: 'self', label: 'Self', Icon: EtImprintMissionsSelfIcon1SVGComp },
  { key: 'client', label: 'Client', Icon: EtImprintMissionsClientsIcon1SVGComp },
  { key: 'org', label: 'Org', Icon: EtImprintMissionOrgIcon1SVGComp },
  { key: 'team', label: 'Team', Icon: EtImprintMissionTeamIcon1SVGComp },
  { key: 'partner', label: 'Partner', Icon: EtImprintMissionPartnerIcon1SVGComp }
  ].filter(Boolean); // 🔑 this removes the 'all' tab if the condition is false

  // console.log('focusTabs', focusTabs)


  // 16 Sep 2025 - Recursive helper to count all actions in nested groups
  const countNestedActions = (node) => {
    if (Array.isArray(node)) return node.length;
    if (typeof node === "object" && node !== null) {
      return Object.values(node).reduce((acc, val) => acc + countNestedActions(val), 0);
    }
    return 0;
  };

  // 16 Sep 2025 - Precompute top-level counts once
  const topLevelCounts = Object.fromEntries(
    Object.entries(groupedUserActions || {}).map(([groupKey, groupVal]) => [
      groupKey,
      countNestedActions(groupVal),
    ])
  );

  // 16 Sep 2025 - focus Counts
  const focusCounts = Object.fromEntries(
    Object.entries(groupedUserActions["MyMissions"] || {}).map(([focusKey, focusGroup]) => [
      focusKey.toLowerCase(),
      countNestedActions(focusGroup)
    ])
  );

  // Add "all" manually as the total sum of all focus groups
  // focusCounts["all"] = countNestedActions(groupedUserActions["MyMissions"] || {});
  focusCounts["all"] = myMissionsRootActions.reduce((sum, [_, arr]) => sum + arr.length, 0);


  // 6b - Filter userActionsData based on activeTab and user-specific conditions
  const filteredUserActionsData = userActionsData?.ids
  .map((id) => userActionsData.entities[id])
  .filter((action) => {
    // Common conditions for all tabs
    const commonConditions =
      action.userId._id === userId &&
      action.trailId._id === trailId &&
      action.sessionId._id === sessionId;

    // Filter based on activeTab value
    if (activeTab === 'Self') {
      return commonConditions && action.actionCreatedBy === 'Self';
    } else if (activeTab === 'Coach') {
      return commonConditions && action.actionCreatedBy === 'Coach';
    } else if (activeTab === 'Sponsor') {
      return commonConditions && action.actionCreatedBy === 'Sponsor';
    }else if (activeTab === 'Session') {
      return commonConditions && action.actionCreatedBy === 'Session';
    } else {
      // Show all actions when the 'All' tab is active
      return commonConditions;
    }
  });

  // console.log("filtered user action data:", filteredUserActionsData)
  // console.log("filtered user actions count: ", filteredUserActionsData?.length)

  // 6c - 28 Sep 2025 - For recurring Action Log note and progress changes
  const getActionLogKey = (actionId, logDate) => `${actionId}__${logDate}`;
  
  // 7 - Handle Note Change -- 09 Jun 2024
  const handleNoteChangeOld = (e, actionId) => {
    const { value } = e.target
    setActionNotes((prevNotes) => ({ ...prevNotes, [actionId]: value }))

    // 18 Sep 2024 - Set the actionProgress to the current userActionProgress if not already set
    setActionProgress((prevProgress) => {
      if (prevProgress[actionId] === undefined) {
          const action = filteredUserActionsData?.find((a) => a.id === actionId);
          return { ...prevProgress, [actionId]: action?.userActionProgress || 0 };
      }
      return prevProgress;
  });
  }

  // 28 Sep 2025 - refined for recurring action logs
  const handleNoteChange = (e, actionId, logDate) => {
    const { value } = e.target
    const key = logDate ? getActionLogKey(actionId, logDate) : actionId; // 28 Sep 2025
    // setActionNotes((prevNotes) => ({ ...prevNotes, [actionId]: value }))
    setActionNotes((prevNotes) => ({ ...prevNotes, [key]: value })) // 28 Sep 2025

    // 18 Sep 2024 - Set the actionProgress to the current userActionProgress if not already set
    setActionProgress((prevProgress) => {
      // if (prevProgress[actionId] === undefined) {
      if (prevProgress[key] === undefined) {                // 28 Sep 2025 - changed from actionId to key
          const action = filteredUserActionsData?.find((a) => a.id === actionId);

          // For recurring actions, use currentLog.progress
          if (logDate && action?.actionRecurrencePattern !== "OneTime") {
            const log = action?.recurringActionLog?.find((l) =>
              moment(l?.logDate).format("YYYY-MM-DD") === logDate
            );
            return { ...prevProgress, [key]: log?.progress || 0 };
          }

          // For OneTime actions, use overall action progress
          return { ...prevProgress, [key]: action?.userActionProgress || 0 };

          // return { ...prevProgress, [actionId]: action?.userActionProgress || 0 };
      }
      return prevProgress;
    });
  }

  // 8 - Handle Progress Change -- 09 Jun 2024
  const handleProgressChangeOld = (e, actionId) => {
    const { value } = e.target
    setActionProgress((prevProgress) => ({
      ...prevProgress,
      [actionId]: value,
    }))
    e.target.style.setProperty("--value", value)
  }

  // 28 Sep 2025 - refined for recurring action logs
  const handleProgressChange = (e, actionId, logDate) => {
    const { value } = e.target
    const key = logDate ? getActionLogKey(actionId, logDate) : actionId; // 28 Sep 2025

    setActionProgress((prevProgress) => ({
      ...prevProgress,
      // [actionId]: value,
      [key]: parseInt(value), // 28 Sep 2025
    }))
    e.target.style.setProperty("--value", value)
  }

  // 8a - UseEffect for slider -- 10 Jun 2024, updated -- 12 Jun 2024 --
  // useEffect(() => {
  //   // Adjust slider initialization to use grouped actions
  //   Object.values(groupedUserActions).flat().forEach((action) => {
  //     const slider = document.getElementById(`slider-${action._id}`);
  //     if (slider) {
  //       const initialValue =
  //         actionProgress[action._id] || action.userActionProgress || 0;
  //       slider.style.setProperty("--value", initialValue);
  //       slider.style.setProperty("--min", 0);
  //       slider.style.setProperty("--max", 100);
  //     }
  //   });
  // }, [groupedUserActions, actionProgress]);
  
  useEffect(() => {
    // Iterate through all actions to set initial styles
    filteredUserActionsData?.forEach((action) => {
      const slider = document.getElementById(`slider-${action.id}`)
      if (slider) {
        const initialValue =
          actionProgress[action.id] || action.userActionProgress || 0
          // console.log('actionProgress[action.id]', actionProgress[action.id], 'action.id', action.id)
        slider.style.setProperty("--value", initialValue)
        slider.style.setProperty("--min", 0)
        slider.style.setProperty("--max", 100)
      }
    })
  }, [filteredUserActionsData, actionProgress]) // Dependencies updated

  // 9 - Save function to update user action - 26 Sep 2025 - changed to include inline progress and actual value updates for recurring actions as well
  const handleSave = async ({
    action, 
    noteText, 
    progressValue, 
    actualValue = 0
  
  }) => {
  
    // 1 - Destructure the fields from action 
    const {
      _id: actionId,
      userId,
      trailId,
      sessionId,
      actionTypeId,
      userActionName,
      userActionDesc,
      completeBy,
      actionRecurrencePattern,
      recurringActionLog = [],
    } = action;

    // 1️⃣ Validate input
    if (!noteText || noteText.trim() === "") {
      alert("Action note cannot be empty");
      return;
    }

    const isRecurring = actionRecurrencePattern !== "OneTime";
    const noteDate = new Date().toISOString();

    // 2️⃣ Handle OneTime actions
    if (!isRecurring) {
      try {
        const updatedAction = {
          id: actionId,
          userId,
          trailId,
          sessionId,
          actionTypeId,
          userActionName,
          userActionDesc,
          completeBy,
          actualValue, // ✅ added actualValue
          userActionNotes: [
            {
              actionNoteText: noteText,
              actionNoteDate: noteDate,
              actionNoteProgress: progressValue,
              actionNoteActualValue: actualValue, // ✅ added
            },
          ],
          userActionProgress: progressValue,
        };

        await updateUserAction(updatedAction).unwrap();
        alert("Action updated successfully");
        // window.location.reload();
        refetchUserActions();
      } catch (error) {
        console.error("❌ Failed to update action:", error);
        alert("Failed to update action");
      }

      return;
    }

    // 3️⃣ Handle Recurring actions

     if (isRecurring) {
      console.log("recurring action progress update.....")
    
      // 3a - Find today's recurring log slot
      // const today = new Date().toISOString().split("T")[0];

      // 30 Dec 2025 - new
      const currentLog = getCurrentRecurrenceLogEntry(action);
      
      // 30 Dec 2025 - commented block - start
      // const today = moment().tz(timezone).format("YYYY-MM-DD"); // 
      // const currentLog = recurringActionLog.find((log) =>
      //   log?.logDate?.startsWith(today)
      // );
      // 30 Dec 205 - commented block end

      if (!currentLog) {
        alert("Could not find a valid recurrence slot for today.");
        return;
      }

      // 3b - Compute scaled progress
      const totalLogs = recurringActionLog.length || 1;
      const logWeight = 100 / totalLogs;
      const scaledProgress = (progressValue / 100) * logWeight;

      // 3c - Recalculate overall progress and cumulative actualValue
      let totalScaledProgress = 0; 
      let cumulativeActualValue = 0;

      // 30 Dec 2025
      for (const log of recurringActionLog) {
        const isCurrentLog = currentLog && log._id.toString() === currentLog._id.toString();

        const progress = isCurrentLog
          ? scaledProgress
          : ((Number(log.progress || 0) / 100) * logWeight);

        const pastActualValue = Number(log.actualValue || 0);

        totalScaledProgress += progress;
        if (!isCurrentLog) {
          cumulativeActualValue += pastActualValue;
        }
      }

      // for (const log of recurringActionLog) {
      //   const isToday = log.logDate?.startsWith(today);
      //   const progress = isToday ? scaledProgress : ((Number(log.progress || 0) / 100) * logWeight);
      //   const pastActualValue = Number(log.actualValue || 0);
        
      //   totalScaledProgress += progress;
      //   if (!isToday) {
      //     cumulativeActualValue += pastActualValue; // exclude today
      //   }
      // }

      const currentActualValue = Number(actualValue);
      cumulativeActualValue += currentActualValue;
 
      // 3d - Final payload
      const updatedRecurringAction = {
        id: actionId,
        userId,
        trailId,
        sessionId,
        actionTypeId,
        userActionName,
        userActionDesc,
        completeBy,
        recurringLogDate: currentLog.logDate,
        recurringActualValue: currentActualValue,
        progressNote: noteText,
        progress: progressValue,
        actualValue: cumulativeActualValue,
        userActionProgress: Math.round(totalScaledProgress),
        userActionNotes: [
          {
            actionNoteText: noteText,
            actionNoteDate: noteDate,
            // actionNoteProgress: Math.round(scaledProgress), // issue
            actionNoteProgress: Math.round(totalScaledProgress), // 27 Sep 2025 - fixed
            actionNoteActualValue: cumulativeActualValue, // ✅ FIX 18 Dec 2025
          },
        ],
      };

      try {
        await updateUserAction(updatedRecurringAction).unwrap();
        alert("Recurring action updated successfully");
        // window.location.reload();
        refetchUserActions();
        // 🧼 Clear local note state (but retain progress)
        const logDateKey = action.actionRecurrencePattern === "OneTime"
          ? null
          : moment().format("YYYY-MM-DD");

        const valueKey = action.actionRecurrencePattern === "OneTime"
          ? action._id
          : `${action._id}__${logDateKey}`;

        setActionNotes((prev) => {
          const next = { ...prev };
          delete next[valueKey]; // ⬅️ only delete the note entry
          return next;
        });
      } catch (error) {
        console.error("❌ Failed to update recurring action:", error);
        alert("Failed to update recurring action");
      }
    }
  };

  //26 Sep 2025 - Prior code - commented
  // const handleSave = async (
  //   userId,
  //   trailId,
  //   sessionId,
  //   actionTypeId,
  //   actionId,
  //   userActionName,
  //   userActionDesc,
  //   completeBy
  // ) => {
  //   const actionNoteText = actionNotes[actionId]
  //   const actionNoteDate = new Date()

  //   // // Use current overall progress if no progress input was provided
  //   // const userActionProgress = actionProgress[actionId] !== undefined 
  //   //     ? actionProgress[actionId] 
  //   //     : actionId?.userActionProgress || 0;

  //   const userActionProgress = actionProgress[actionId] // 18 Sep 2024 - previous code
  //   // console.log('userActionProgress:', userActionProgress) // 18 Sep 2024 - for testing

  //   // Check if the action note text is not empty
  //   if (!actionNoteText) {
  //     alert("Action note cannot be empty")
  //     return
  //   }

  //   try {
  //     const updatedAction = {
  //       id: actionId,
  //       userId: userId,
  //       trailId: trailId,
  //       sessionId: sessionId,
  //       actionTypeId: actionTypeId,
  //       userActionName: userActionName,
  //       userActionDesc: userActionDesc,
  //       completeBy: completeBy,
  //       userActionNotes: [
  //         {
  //           actionNoteText,
  //           actionNoteDate,
  //           actionNoteProgress: userActionProgress,
  //         },
  //       ],
  //       userActionProgress,
  //     }

  //     await updateUserAction(updatedAction).unwrap()
  //     alert("Action updated successfully")
  //     window.location.reload()
  //   } catch (error) {
  //     console.error("Failed to update action:", error)
  //     alert("Failed to update action")
  //   }
  // }

  // 10a - bgColor for action progress status -- 29 Jun 2024
  
  
  const getBgColor = (action) => {
    const today = new Date() // Current date
    const completeByDate = new Date(action?.completeBy); // Assuming it's already a Date object
    
    const daysDifference = differenceInDays(completeByDate, today);

    let bgColor = 'bg-lime-400'; // default color

    if (daysDifference < 0) {
      bgColor = 'bg-red-600 text-white';
    } else if (daysDifference < 1) {
      bgColor = 'bg-orange-400 text-white';
    } else if (daysDifference < 3) {
      bgColor = 'bg-yellow-400';
    } // default bgColor 'bg-lime-400' will be used if daysDifference > 3
    return bgColor
  }

  // 10b - Icon color for action progress icon  -- 29 Jun 2024
  const getIconClr = (action) => {
    const today = new Date(); // Current date
    const completeByDate = new Date(action?.completeBy); // Convert the string to a Date object
  
    if (isNaN(completeByDate)) {
      console.error('Invalid date:', action?.completeBy);
      return '#84cc16'; // Return the default color if the date is invalid
    }
  
    const daysDifference = differenceInDays(completeByDate, today);
    //console.log('Difference in days', daysDifference);
  
    let color = '#84cc16'; // default color
  
    if (daysDifference < 0) {
      color = '#dc2626';
    } else if (daysDifference < 1) {
      color = '#f97316';
    } else if (daysDifference < 3) {
      color = '#facc15';
    } // default color '#84cc16' will be used if daysDifference > 3
  
    return color;
  };

  // 10c - get the difference in days -- 29 Jun 2024
  const getDaysDifference = (action) => {
    const today = new Date(); // Current date
    const completeByDate = new Date(action?.completeBy); // Convert the string to a Date object
    const daysDifference = differenceInDays(completeByDate, today);
    return daysDifference
  }

  // 10d - performance data display index - 22 Dec 2025
  const performanceIndex = useMemo(() => {
    return buildPerformanceIndex(allFilteredActions);
  }, [allFilteredActions]);

  // console.log(performanceIndex.Self.Action);
  // console.log(performanceIndex.Collective.Action);
  

  // 11 - Optional: handle loading and error states
  if (isLoading) return <PulseLoader color={"#ea580c"} />
  if (isError) return <div>Error occurred: {error?.data?.message}</div>

  return (
    <div className="m-1 p-1 rounded font-poppins xl:mx-auto">
      <div className="bg-white m-1 p-1 rounded">
        {/* 11 Sep 2024 - Step 2: Add Tabs for filtering actions */}
          <div className="flex justify-start space-x-4 p-2 border-b font-poppins text-sm">
            <button
              className={`px-3 py-1 ${activeTab === 'All' ? 'text-orange-600 border-b-2 font-bold border-orange-600' : 'text-gray-500'}`}
              onClick={() => setActiveTab('All')}
            >
              All
            </button>
            <button
              className={`px-3 py-1 ${activeTab === 'Self' ? 'text-orange-600 border-b-2 font-bold border-orange-600' : 'text-gray-500'}`}
              onClick={() => setActiveTab('Self')}
            >
              Self
            </button>
            <button
              className={`px-3 py-1 ${activeTab === 'Coach' ? 'text-orange-600 border-b-2 font-bold border-orange-600' : 'text-gray-500'}`}
              onClick={() => setActiveTab('Coach')}
            >
              Coach
            </button>
            <button
              className={`px-3 py-1 ${activeTab === 'Sponsor' ? 'text-orange-600 border-b-2 font-bold border-orange-600' : 'text-gray-500'}`}
              onClick={() => setActiveTab('Sponsor')}
            >
              Sponsor
            </button>

            <button
              className={`px-3 py-1 ${activeTab === 'Session' ? 'text-orange-600 border-b-2 font-bold border-orange-600' : 'text-gray-500'}`}
              onClick={() => setActiveTab('Session')}
            >
              Session
            </button>
          </div>

        {/* 11 Jun 2024 - Added this section when there are no user actions in the trail*/}
        {filteredUserActionsData?.length === 0 ? (
          <div className="m-1 p-1"><p className="text-center text-orange-600 text-xs">
            There are no user actions added to this tab yet
          </p></div>
        ) : (
                //  13 Sep 2025 Refactored Display code
          <>
            {/* Step 1: Top Level Tabs – MyMissions / Induction */}
            <div className="flex justify-start space-x-4 px-3 py-2 border-b font-poppins">
              {topLevelGroups.map((groupKey) => {
                return(
                <button
                  key={groupKey}
                  className={`font-semibold px-1 xl:px-3 py-1 rounded-t ${
                    topLevelTab === groupKey
                      ? 'bg-orange-200 text-orange-800' : 'text-gray-500'
                  }`}
                  onClick={() => setTopLevelTab(groupKey)}
                >
                  <div className="flex items-center space-x-2 px-1 mx-1">
                    <p className="text-sm font-medium">{groupKey}</p>
                    <div className="rounded-full flex justify-center items-center h-5 w-5 border border-orange-300 bg-gray-50">
                      <p className="text-orange-600 text-[10px]">
                        {topLevelCounts[groupKey] || 0}
                      </p>
                    </div>
                  </div>
                </button>
              )})}
            </div>

            {/* Step 2a: Focus Tabs – Only show for Induction */}

            {topLevelTab === "Induction" && (
              <>
                {/* 🔶 Induction Level Action Groups */}
                <div className="ml-2 mt-3">
                  {Object.entries(groupedUserActions["Induction"] || {}).length > 0 && (
                    <EtGroupedActionTypeBlock
                      actionTypeEntries={Object.entries(groupedUserActions["Induction"])}
                      performanceIndex={performanceIndex}   // ✅ 22 Dec 2025 - NEW
                      showGroup={showGroup}
                      setShowGroup={setShowGroup}
                      actionNotes={actionNotes}
                      actionProgress={actionProgress}
                      showDetails={showDetails}
                      handleToggleDetails={handleToggleDetails}
                      handleNoteChange={handleNoteChange}
                      handleProgressChange={handleProgressChange}
                      handleSave={handleSave}
                      setSelectedAction={setSelectedAction}
                      setShowEditModal={setShowEditModal}
                      setSelectedActionToDelete={setSelectedActionToDelete}
                      setShowDeleteModal={setShowDeleteModal}
                      userId={userId}
                      trailId={trailId}
                      sessionId={sessionId}
                      // actionActualValue={actionActualValue} // 26 Sep 2025
                      // setActionActualValue={setActionActualValue} // 26 Sep 2025
                    />
                  )}
                </div>
              </>
            )}

            {/* Step 2b: Focus Tabs – Only show for MyMissions */}
            {topLevelTab === "MyMissions" && (
              <>
                {/* Focus Tab Bar */}
                <div className="flex space-x-4 border-b border-orange-500 mt-3 mb-4 overflow-x-auto px-3">
                  {/* {[
                    { key: 'all', label: 'All', Icon: EtImprintMissionsIcon1SVGComp }, // 15 Sep 2025
                    { key: 'self', label: 'Self', Icon: EtImprintMissionsSelfIcon1SVGComp },
                    { key: 'client', label: 'Client', Icon: EtImprintMissionsClientsIcon1SVGComp },
                    { key: 'org', label: 'Org', Icon: EtImprintMissionOrgIcon1SVGComp },
                    { key: 'team', label: 'Team', Icon: EtImprintMissionTeamIcon1SVGComp },
                    { key: 'partner', label: 'Partner', Icon: EtImprintMissionPartnerIcon1SVGComp }
                  ].map(({ key, label, Icon }) => ( */}
                  {focusTabs.map(({ key, label, Icon }) => (
                    <button
                      key={key}
                      onClick={() => setSelectedMissionFocusTab(key)}
                      className={`flex items-center space-x-2 px-4 py-2 text-sm font-semibold border-b-2 ${
                        selectedMissionFocusTab === key
                          ? "border-orange-600 text-orange-600"
                          : "border-transparent text-gray-500"
                      }`}
                    >
                      <div
                        className={`h-8 w-8 flex justify-center items-center rounded-full cursor-pointer ${
                          selectedMissionFocusTab === key
                            ? "bg-gradient-to-r from-orange-600 to-orange-600 via-orange-800 hover:from-orange-400 hover:via-orange-600 hover:to-black shadow-lg shadow-black"
                            : "bg-gray-100 hover:bg-orange-100"
                        }`}
                      >
                        <Icon
                          className={`h-5 w-5 ${
                            selectedMissionFocusTab === key ? "text-white" : "text-orange-600"
                          }`}
                        />
                      </div>
                      <p className="text-sm font-medium">{label}</p>
                      <div className="rounded-full flex justify-center items-center h-5 w-5 border border-orange-300 bg-gray-50">
                        <p className="text-orange-600 text-[10px]">
                          {focusCounts[key] || 0}
                        </p>
                      </div>
                      {/* <span>{label}</span> */}
                    </button>
                  ))}
                </div>

                {/* Step 3: Render Focus Group content – only selected one */}
                <div className="ml-2 mt-3">

                  {selectedMissionFocusTab === "all" && hasMyMissionsRootActions && (
                    // ✅ Render ALL top-level MyMissions actions (not linked to any focus)
                    Object.entries(groupedUserActions[topLevelTab] || {})
                      .filter(
                        ([_, val]) => 
                        Array.isArray(val) 
                    ).length > 0 && (
                        <EtGroupedActionTypeBlock
                          actionTypeEntries={myMissionsRootActions}
                          performanceIndex={performanceIndex}   // ✅ 22 Dec 2025 - NEW
                          showGroup={showGroup}
                          setShowGroup={setShowGroup}
                          actionNotes={actionNotes}
                          actionProgress={actionProgress}
                          showDetails={showDetails}
                          handleToggleDetails={handleToggleDetails}
                          handleNoteChange={handleNoteChange}
                          handleProgressChange={handleProgressChange}
                          handleSave={handleSave}
                          setSelectedAction={setSelectedAction}
                          setShowEditModal={setShowEditModal}
                          setSelectedActionToDelete={setSelectedActionToDelete}
                          setShowDeleteModal={setShowDeleteModal}
                          userId={userId}
                          trailId={trailId}
                          sessionId={sessionId}
                          // actionActualValue={actionActualValue} // 26 Sep 2025
                          // setActionActualValue={setActionActualValue} // 26 Sep 2025
                        />
                      )
                    )}
                  
                  {Object.entries(groupedUserActions[topLevelTab] || {})
                    .filter(([focusKey]) => focusKey.toLowerCase() === selectedMissionFocusTab)
                    .map(([focusKey, visionGroups]) => {
                      return (
                      <div key={focusKey}>
                        {/* ✅ EtGroupedLevelBlock Entry Point: Vision */}
                        {Object.entries(visionGroups).map(([visionName, visionOrActions]) => (
                          <EtGroupedLevelBlock
                            key={visionName}
                            levelName="Vision"
                            levelKey={visionName}
                            levelOrActions={visionOrActions}
                            performanceIndex = {performanceIndex}   // ✅ NEW 22 Dec 2025
                            iconComponent={EtImprintVisionIcon2SVGComp}
                            showState={showVision}
                            setShowState={setShowVision}
                            nextLevelLabel="Mission"
                            
                            // ✅ Pass full master prop list
                            showGroup={showGroup}
                            setShowGroup={setShowGroup}
                            actionNotes={actionNotes}
                            actionProgress={actionProgress}
                            showDetails={showDetails}
                            handleToggleDetails={handleToggleDetails}
                            handleNoteChange={handleNoteChange}
                            handleProgressChange={handleProgressChange}
                            handleSave={handleSave}
                            setSelectedAction={setSelectedAction}
                            setShowEditModal={setShowEditModal}
                            setSelectedActionToDelete={setSelectedActionToDelete}
                            setShowDeleteModal={setShowDeleteModal}
                            userId={userId}
                            trailId={trailId}
                            sessionId={sessionId}
                            // getBgColor={getBgColor}
                            // getIconClr={getIconClr}
                            // getDaysDifference={getDaysDifference}



                            renderNextLevelBlock={({ levelKey, levelOrActions }) => (
                              <EtGroupedLevelBlock
                                levelName="Mission"
                                levelKey={levelKey}
                                levelOrActions={levelOrActions}
                                performanceIndex = {performanceIndex}   // ✅ NEW 22 Dec 2025
                                iconComponent={EtImprintMissionsIcon1SVGComp}
                                showState={showMission}
                                setShowState={setShowMission}
                                nextLevelLabel="Goal"

                                // 🔁 Repeat full props
                                showGroup={showGroup}
                                setShowGroup={setShowGroup}
                                actionNotes={actionNotes}
                                actionProgress={actionProgress}
                                showDetails={showDetails}
                                handleToggleDetails={handleToggleDetails}
                                handleNoteChange={handleNoteChange}
                                handleProgressChange={handleProgressChange}
                                handleSave={handleSave}
                                setSelectedAction={setSelectedAction}
                                setShowEditModal={setShowEditModal}
                                setSelectedActionToDelete={setSelectedActionToDelete}
                                setShowDeleteModal={setShowDeleteModal}
                                userId={userId}
                                trailId={trailId}
                                sessionId={sessionId}
                                // getBgColor={getBgColor}
                                // getIconClr={getIconClr}
                                // getDaysDifference={getDaysDifference}


                                renderNextLevelBlock={({ levelKey, levelOrActions }) => (
                                  <EtGroupedLevelBlock
                                    levelName="Goal"
                                    levelKey={levelKey}
                                    levelOrActions={levelOrActions}
                                    performanceIndex = {performanceIndex}   // ✅ NEW 22 Dec 2025
                                    iconComponent={EtUserAcionGoalIcon1SVGComp}
                                    showState={showGoal}
                                    setShowState={setShowGoal}
                                    nextLevelLabel="Goal Period"

                                    // 🔁 Repeat full props
                                    showGroup={showGroup}
                                    setShowGroup={setShowGroup}
                                    actionNotes={actionNotes}
                                    actionProgress={actionProgress}
                                    showDetails={showDetails}
                                    handleToggleDetails={handleToggleDetails}
                                    handleNoteChange={handleNoteChange}
                                    handleProgressChange={handleProgressChange}
                                    handleSave={handleSave}
                                    setSelectedAction={setSelectedAction}
                                    setShowEditModal={setShowEditModal}
                                    setSelectedActionToDelete={setSelectedActionToDelete}
                                    setShowDeleteModal={setShowDeleteModal}
                                    userId={userId}
                                    trailId={trailId}
                                    sessionId={sessionId}
                                    // getBgColor={getBgColor}
                                    // getIconClr={getIconClr}
                                    // getDaysDifference={getDaysDifference}




                                    renderNextLevelBlock={({ levelKey, levelOrActions }) => (
                                      <EtGroupedLevelBlock
                                        levelName="Goal Period"
                                        levelKey={levelKey}
                                        levelOrActions={levelOrActions}
                                        performanceIndex = {performanceIndex}   // ✅ NEW 22 Dec 2025
                                        iconComponent={CalendarDaysIcon}
                                        showState={showGoalPeriod}
                                        setShowState={setShowGoalPeriod}
                                        nextLevelLabel={null}

                                        // 🔁 Repeat full props
                                        showGroup={showGroup}
                                        setShowGroup={setShowGroup}
                                        actionNotes={actionNotes}
                                        actionProgress={actionProgress}
                                        showDetails={showDetails}
                                        handleToggleDetails={handleToggleDetails}
                                        handleNoteChange={handleNoteChange}
                                        handleProgressChange={handleProgressChange}
                                        handleSave={handleSave}
                                        setSelectedAction={setSelectedAction}
                                        setShowEditModal={setShowEditModal}
                                        setSelectedActionToDelete={setSelectedActionToDelete}
                                        setShowDeleteModal={setShowDeleteModal}
                                        userId={userId}
                                        trailId={trailId}
                                        sessionId={sessionId}
                                        // getBgColor={getBgColor}
                                        // getIconClr={getIconClr}
                                        // getDaysDifference={getDaysDifference}

                                        renderNextLevelBlock={null}
                                      />
                                    )}
                                  />
                                )}
                              />
                            )}
                          />
                        ))}

                      </div>
                    )})}
                </div>
              </>
            )}

          </>

        )}
      </div>
      {/* 16 May 2025 - Edit Action Modal */}
      {showEditModal && selectedAction && (
        <EtModalUpdateUserAction
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false)
            setSelectedAction(null)
          }}
          actionId={selectedAction.id}
          actionData={selectedAction}
          // refetchUserActions={() => window.location.reload()} 
          refetchUserActions={refetchUserActions} // changed to refetch
        />
      )}
      {/* 16 May 2025 - Delete Action Confirmation Modal */}
      {showDeleteModal && selectedActionToDelete && (
        <Modal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          title="Confirm Delete"
          message={`Are you sure you want to delete the action: "${selectedActionToDelete?.userActionName}"? This cannot be undone.`}
          onConfirm={async () => {
            try {
              await deleteUserAction({ id: selectedActionToDelete.id }).unwrap();
              setShowDeleteModal(false);
              setSelectedActionToDelete(null);
              refetchUserActions();
            } catch (err) {
              console.error("Failed to delete user action", err);
              alert("Failed to delete the action.");
            }
          }}
        />

      )}
    </div>
  )
}

export default EtUserActionListByGroupWithTabsComp
